/**
 * Component: Inline Product
 * -----------------------------------------------------------------------------
 * Logic related to Inline Product.
 * @namespace InlineProduct
 */

import { on } from '~/helpers/utils'
import classes from '~/config/classes'

/**
 * DOM selectors.
 */
const selectors = {
  image: '[js-inline-product="image"]',
  title: '[js-inline-product="title"]',
  price: '[js-inline-product="price"]',
  swatches: '[js-inline-product="swatch"]',
  atc: '[js-inline-product="atc"]',
  link: '[js-inline-product="link"]',
  cartDrawer: '[data-drawer="drawer-cart"]',
}

/**
 * Create a new Inline Product object.
 */
export default () => {

  /**
   * Node Elements.
   */
  const nodes = {
    image: document.querySelector(selectors.image),
    title: document.querySelector(selectors.title),
    price: document.querySelector(selectors.price),
    swatches: document.querySelectorAll(selectors.swatches),
    link: document.querySelector(selectors.link),
    atc: document.querySelector(selectors.atc),
    cartDrawer: document.querySelector(selectors.cartDrawer),
  }

  /**
   * Initialize component.
   */
  async function init() {
    setEventListeners()
    const meta = await getSwatchMeta()
    styleSwatches(meta)
  }

  /**
   * Set Event Listeners.
   */
  function setEventListeners() {
    nodes.swatches.forEach((swatch) => {
      on('click', swatch, activateSwatch)
    })

    on('click', nodes.atc, addToCart)
  }

  /**
   * Activate swatch
   * - swap main image
   * - update price
   * - update link
   * @param {Event} event - click event
   */
  function activateSwatch(event) {
    const { image, price, url, id, available } = event.target.dataset

    nodes.swatches.forEach((swatch) => swatch.classList.remove(classes.active))

    nodes.image.src = image
    nodes.price.innerText = price
    nodes.atc.dataset.id = id
    nodes.link.href = url
    event.target.classList.add(classes.active)

    if (available) {
      nodes.atc.removeAttribute('disabled')
    } else {
      nodes.atc.setAttribute('disabled', true)
    }
  }

  /**
   * Fetch swatch metadata.
   * @returns {Object, Boolean}
   */
  async function getSwatchMeta() {
    try {
      const response = await fetch(window.theme.assets.swatches)
      const jsonData = await response.json()

      if (!jsonData.colors) {
        return false
      }

      return jsonData.colors
    } catch (error) {
      console.error('Failed to load swatch meta', error)
    }

    return false
  }

  /**
   * Style swatches with appropriate color/image.
   * @param {Object} meta - Swatch metadata
   */
  function styleSwatches(meta) {
    nodes.swatches.forEach((swatch) => {
      const name = swatch.dataset.swatch.toLowerCase()
      const swatchData = meta.find((data) => Object.keys(data)[0] === name)

      if (!swatchData) {
        return
      }

      const color = Object.values(swatchData)[0]

      if (
        color.includes('.jpg') ||
        color.includes('.jpeg') ||
        color.includes('.png') ||
        color.includes('.svg')
      ) {
        swatch.style.setProperty('background-image', `url(${window.theme.assets.base}${color})`)
      } else {
        swatch.style.setProperty('background-color', `${color}`)
      }

      swatch.classList.remove(classes.loading)
    })
  }

  /**
   * Add product to cart
   */
  async function addToCart() {
    const id = nodes.atc.dataset.id
    const body = {
      id,
      quantity: 1,
    }

    nodes.atc.setAttribute('disabled', true)
    nodes.atc.classList.add(classes.loading)
    nodes.atc.innerHTML = window.theme.strings.addingToCart

    await fetch(`${window.theme.routes.cart_add_url}.js`, {
      body: JSON.stringify(body),
      credentials: 'same-origin',
      headers: {
        'Content-Type': 'application/json',
        'X-Requested-With': 'xmlhttprequest',
      },
      method: 'POST',
    })
      .then(() => {
        onAddSuccess()
      })
      .catch((err) => {
        console.error('Failed to add item to cart', err)
      })

    nodes.atc.removeAttribute('disabled')
    nodes.atc.classList.remove(classes.loading)
    nodes.atc.innerHTML = window.theme.strings.addToCart
  }

  /**
   * Handle cart drawer UI after add to cart.
   */
  function onAddSuccess() {
    fetch(`${window.theme.routes.cart}.js`)
      .then((response) => {
        document.dispatchEvent(
          new CustomEvent('theme:cart:change', {
            detail: {
              cart: response.data,
            },
            bubbles: true,
          }),
        )

        document.dispatchEvent(new CustomEvent('theme:cart:reload', { bubbles: true }))
        nodes.cartDrawer.dispatchEvent(new CustomEvent('theme:drawer:open', { bubbles: false }))
      })
      .catch((error) => {
        console.error(error)
      })
  }

  /**
   * Expose public interface.
   */
  return Object.freeze({
    init,
  })
}
